import { Stack, Typography, Avatar, Button, Box, Divider } from '@mui/material';
import { Fragment, useState } from 'react';
import { uniqueId } from 'lodash';
import { getInitials } from '@/utils/getInitials';
import { useNotesContext } from '../../hooks/useNotesContext';
import { TNoteMessage } from '../../types';
import { renderHighlightedTextWithHTML } from '../../utils';
import { formatDate } from '../note';
import { Gallery } from '@/components/common/Gallery/Gallery';
import WaveSurferPlayer from '@/components/common/AudioPlayer/WaveSurferPlayer';
import { EditMenu } from '../EditMenu';
import { useMediaUrls } from './hooks/use-media-urls';

type Props = {
  message: TNoteMessage;
  form: React.ReactNode;
};

export function NoteMessage({ message, form }: Props) {
  const { userId, collaborators, noteToEdit, isGuest, isRtl } = useNotesContext();

  const isModified =
    new Date(message?.createdAt).getTime() !== new Date(message?.updatedAt).getTime();
  const messageAuthor = collaborators?.find((c) => c.id === message?.userId);

  const [isExpanded, setIsExpanded] = useState(false);
  const stripHtmlTags = (str: string) => {
    return str.replace(/<[^>]*>/g, '');
  };

  const descriptionText = stripHtmlTags(message?.description || '');
  const isLongText = descriptionText?.length > 100;
  const { audioMessages, imageMessages } = useMediaUrls(message?.mediaUrls);

  return (
    <Stack key={message?.id} direction="row" flexWrap="wrap" width="100%" px="18px">
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          background: 'white',
          border: '1px solid #EAECF0',
          borderRadius: '8px',
          width: '100%',
          padding: '14px',
        }}
      >
        <Stack width="100%" direction="row" alignItems="center" gap={1} py={1}>
          <Avatar
            sx={{
              height: '40px',
              width: '40px',
              img: { objectFit: 'contain' },
              bgcolor: userId === messageAuthor?.id ? '#2055ff' : 'white',
              color: userId === messageAuthor?.id ? 'white' : 'black',
              border: '1px solid #D1D1D1',
            }}
          >
            {getInitials(messageAuthor?.name || 'Guest')}
          </Avatar>
          <Stack direction="column">
            <Typography
              sx={{
                fontSize: '14px',
                color: '#101828',
                fontWeight: '550',
              }}
            >
              {messageAuthor?.name || 'Guest'}
            </Typography>
            <Stack direction="row">
              <Typography
                variant="caption"
                sx={{
                  fontSize: '14px',
                  color: '#667085',
                }}
              >
                {formatDate(message?.updatedAt)}
              </Typography>
              {isModified && (
                <>
                  <Divider orientation="vertical" sx={{ marginX: '5px', height: '15px' }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: '#667085',
                    }}
                  >
                    Edited
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="center" alignItems="center" gap={1} ml="auto">
            {message?.userId === userId && <EditMenu message={message} />}
          </Stack>
        </Stack>
        {message?.description && (
          <Box sx={{ display: 'inline' }}>
            <Typography
              variant="body1"
              width="100%"
              sx={{
                maxWidth: '400px',
                display: 'inline',
                textAlign: isRtl ? 'right' : 'left',
                color: '#101828',
                fontSize: '14px',
                lineHeight: '20px',
                wordBreak: 'break-all',
                whiteSpace: 'normal',
              }}
              dangerouslySetInnerHTML={
                isExpanded || !isLongText
                  ? renderHighlightedTextWithHTML(
                      message?.description === 'null' ? '<span></span>' : message?.description,
                      collaborators,
                    )
                  : renderHighlightedTextWithHTML(
                      `${message?.description.slice(0, 100)}...`,
                      collaborators,
                    )
              }
            />
            {isLongText && (
              <Button
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  display: 'inline',
                  textTransform: 'none',
                  padding: 0,
                  minHeight: 0,
                  minWidth: 0,
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: 'black',
                  '&:hover': {
                    color: 'black',
                    textDecoration: 'underline',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {isExpanded ? '...Read less' : 'Read more'}
              </Button>
            )}
          </Box>
        )}
        {!!audioMessages?.length &&
          audioMessages.map((url) => {
            return (
              <Fragment key={uniqueId()}>
                <WaveSurferPlayer
                  url={`${process.env.NEXT_PUBLIC_CDN}/${url}`}
                  waveColor="#D0D5DD"
                  progressColor="#2055FF"
                  barWidth={2}
                  barGap={2}
                  barRadius={4}
                  barHeight={10}
                  wavefHeight={33}
                  playPauseButtonSx={{ color: '#2055FF', padding: 0, height: '30px' }}
                  containerSx={{
                    padding: '10px',
                    height: '70px',
                    maxHeight: '70px',
                    width: '100%',
                    gap: '6px',
                    background: '#F9FAFB',
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                  }}
                />
              </Fragment>
            );
          })}
        {!!imageMessages?.length && (
          <Box width="320px" margin="0 auto">
            <Gallery
              items={imageMessages}
              settings={{
                fullScreen: false,
                imageHeight: 252,
                dotsColor: '#D0D5DD',
                thumbnailIconBackground: '#F4F5F2',
                thumbnailBorderColor: '#D1D1D1',
                thumbnailSize: 70,
                fullscreenByClick: true,
                alternativeArrows: false,
                thumbnails: imageMessages.length > 1,
                dots: imageMessages.length > 1,
                isNotesGallery: true,
              }}
            />
          </Box>
        )}
      </Stack>
      {!!noteToEdit && message.id === noteToEdit?.message?.id && userId && !isGuest && (
        <Stack width="100%" sx={{ background: '#F1F1F1CC' }}>
          {form}
        </Stack>
      )}
    </Stack>
  );
}
